
import { Component } from "../component.js";
import { loadCache, normalizeUrl, HTML_FRAME_ACCEPT_HEADER, readSerializedMarkup } from "./helpers.js";

const preloading = {};

Component.register('blognami-document', {
    meta(){
        this.include('blognami-frame');

        this.prototype.assignProps({
            get meta(){
                const out = {};
                this.head.findAll('meta').forEach(({ params }) => {
                    const { name, content } = params;
                    if(name) out[name] = content;
                });
                return out;
            }
        })
    },

    initialize(...args){    
        this.constructor.for('blognami-frame').prototype.initialize.call(this, ...args);

        const overlayDefaultPlaceholder = this.head.find('meta[name="blognami-overlay-default-placeholder"]')?.params.content;
        if(overlayDefaultPlaceholder) this.preload(normalizeUrl(overlayDefaultPlaceholder, this.url));
        this.preloadDefaultPlaceholder();

        window.onpopstate = (event) => {
            this.load(event.state || window.location, { replace: true });
        };

        this.lastChange = Date.now();
        this.setInterval(() => {
            if(!this.progressBar) return;
            const now = Date.now();
            if(this.progressBar.startCount > 0) {
                this.logChange();
            } else if(now - this.lastChange > 500) {
                this.html.addClass('idle');
            }
        }, 100);
    },

    logChange(){
        this.lastChange = Date.now();
        this.html.removeClass('idle');
    },

    isDocument: true,

    get html(){
        if(!this._html){
            this._html = this.find('html');
        }
        return this._html;
    },

    get head(){
        if(!this._head){
            this._head = this.find('head');
        }
        return this._head;
    },
    
    get body(){
        if(!this._body){
            this._body = this.find('body');
        }
        return this._body;
    },

    get progressBar(){
        return this.body?.progressBar;
    },

    get loadCacheNamespace(){
        return this.head.find('meta[name="blognami-load-cache-namespace"]')?.params.content ?? 'default';
    },

    get defaultPlaceholder(){
        return this.head.find('meta[name="blognami-default-placeholder"]')?.params.content;
    },

    get globalStyles(){
        if(!this._globalStyles){
            try {
                this._globalStyles = mergeCssStylesheets(this.node.styleSheets ?? []);
            } catch (e) {
                this._globalStyles = undefined;
            }
        }
        return this._globalStyles;
    },

    get componentBase(){
        return this.head.find('meta[name="blognami-component-base"]')?.params.content;
    },

    async load(url = this.url.toString(), options = {}){
        const { replace, method = 'GET' } = options;
        const previousUrl = this.url.toString();
        const normalizedUrl = normalizeUrl(url, previousUrl).toString();

        const previous = new URL(previousUrl);
        const target = new URL(normalizedUrl);
        const isHashOnlyChange = method == 'GET'
            && previousUrl != normalizedUrl
            && previous.origin == target.origin
            && previous.pathname == target.pathname
            && previous.search == target.search;

        if(method == 'GET' && previousUrl != normalizedUrl){
            if(replace){
                history.replaceState(normalizedUrl, null, normalizedUrl);
            } else {
                history.pushState(normalizedUrl, null, normalizedUrl);
                if(!isHashOnlyChange) window.scrollTo(0, 0);
            }
        }

        if(isHashOnlyChange){
            this.url = url;
        } else {
            await this.constructor.for('blognami-frame').prototype.load.call(this, url, options);
        }

        this.preloadDefaultPlaceholder();

        if(!this.url.hash) return;

        const targetElement = this.document.find(this.url.hash);
        if(!targetElement) return;

        let scrollY = targetElement.node.getBoundingClientRect().top + window.scrollY;

        const scrollTopElementId = this.document.head.find('meta[name="blognami-scroll-top-element-id"]')?.params.content || 'blognami-scroll-top';
        const scrollTopElement = this.document.find(`#${scrollTopElementId}`);
        if(scrollTopElement){
            scrollY -= scrollTopElement.node.getBoundingClientRect().bottom + 10;
        }
    
        window.scrollTo(0, scrollY);
    },

    preloadDefaultPlaceholder(){
        if(this.defaultPlaceholder){
            this.preload(normalizeUrl(this.defaultPlaceholder, this.url));
        }
    },

    async preload(url){
        if(loadCache.get(`${this.document.loadCacheNamespace}:${url}`)) return;
        if(preloading[url.toString()]) return;
        preloading[url.toString()] = true;
        const response = await fetch(url, { headers: { Accept: HTML_FRAME_ACCEPT_HEADER } });
        loadCache.put(`${this.document.loadCacheNamespace}:${url}`, await readSerializedMarkup(response));
        delete preloading[url.toString()];
    }
});


export function mergeCssStylesheets(stylesheets, out = new CSSStyleSheet()) {
    for(const sheet of stylesheets){
        try {
            for(const rule of sheet.cssRules) {
                if(rule instanceof CSSImportRule) {
                    mergeCssStylesheets([ rule.styleSheet ], out);
                } else {
                    out.insertRule(rule.cssText);
                }
            }
        } catch (e) {
            // Ignore errors from stylesheets that cannot be merged
        }
    }
    return out;
}